import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Alert, AlertDescription } from "../ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Spinner } from "../ui/spinner";
import { MetadataService } from "../../services/metadataService";

const EditSeverityForm = ({ open, onClose, severity, onSeverityUpdated }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [formData, setFormData] = useState({
    value: "",
    name: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (severity && open) {
      setFormData({
        value: severity.value?.toString() || "",
        name: severity.name || "",
        description: severity.description || "",
      });
      setError(null);
    }
  }, [severity, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!severity) return;

    try {
      setLoading(true);
      setError(null);

      const updateData = {
        value: parseInt(formData.value),
        name: formData.name.trim(),
        description: formData.description.trim(),
      };

      await MetadataService.updateSeverity(
        severity.id,
        updateData,
        getAccessTokenSilently
      );

      onSeverityUpdated();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const isValid =
    formData.value && formData.name.trim() && formData.description.trim();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Severity Level</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="value">Level Value</Label>
            <Input
              id="value"
              name="value"
              type="number"
              min="1"
              max="10"
              value={formData.value}
              onChange={handleInputChange}
              placeholder="Enter level value (1-10)"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter severity name (e.g., Critical, High, Medium, Low)"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter severity description"
              required
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={!isValid || loading}>
              {loading && <Spinner size="sm" className="mr-2" />}
              Update Severity
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditSeverityForm;
